<template>
  <div class="text-dark do-calendar__wrapper" :class="{'do-calendar__wrapper--dragging': isDragging}">
    <div class="do-calendar-frontend-lock" ref="do_calendar_frontend_lock" v-if="isDeviceMobile"
         @click="setIsLockedFromFrontend">
      <div class="do-calendar-frontend-lock__inner">
        <span class="icon-lock5" v-if="isLockedFromFrontend"></span>
        <span class="icon-unlocked2" v-else></span>
      </div>
    </div>
    <week-selector
      :startDate="weekSelectorStartDate"
      :endDate="weekSelectorEndDate"
      :date="activeCalendarDate"
      @changed="setDate">
    </week-selector>
    <div id="printCalendar">
      <div class="d-calendar do-calendar">
        <div class="d-calendar__header do-calendar__header" style="min-height: 54px">
          <DCalendarHeader
            v-if="calendar"
            :isLoadingEvents="isEventsLoading"
            :activeCalendarDate="activeCalendarDate"
            :calendarId="theActiveCalendarId"
            :initCalendar="calendar"
            :dates="calendarDates"
            :hasHotKeys="true"
            @updateEvents="readEvents"
            @updateCalendar="readEvents(true)"
            @next="nextWeek"
            @prev="prevWeek"
            @today="today"
            @copyCalendarModalOpen="setIsCopyCalendarModalOpen($event)"
            :isMultiple="userHasPermission('can_see_other_calendars')">
          </DCalendarHeader>
        </div>
        <div class="d-calendar__calendar__container"
             :class="{'d-calendar__calendar__container--print-mode': isCalendarInPrintMode}">
          <DCalendar
            :calendar="calendar"
            :isEditable="isEditable"
            :isLocked="isLocked"
            :isCalendarInPrintMode="isCalendarInPrintMode"
            :isShowWeekends="isShowWeekends"
            :startTime="startTime"
            :endTime="endTime"
            :date="activeCalendarDate"
            :events="dCalendarEvents"
            :calendarId="theActiveCalendarId"
            :calendarName="calendarName"
            :firstDayOfWeek="firstDayOfWeek"
            :isEventsClickable="true"
            :isAbsenceEditable="getIsAbsenceEditable"
            :isCopyCalendarModalOpen="isCopyCalendarModalOpen"
            @onEventClick="setActiveEvent"
            @dates="setCalendarDates"
            @onDragging="setIsDragging">
          </DCalendar>
        </div>
      </div>
      <DWeekplan
        v-if="isShowWeekPlan"
        :isInPrintMode="isInPrintMode"
        :isReadOnly="isInPrintMode || !userHasPermission('can_edit_own_notes')">
      </DWeekplan>
    </div>
    <transition name="zoomIn">
      <DCalendarShowEvent
        v-if="activeEvent"
        :isDCalendarInROR="true"
        :canEditEvent="true"
        :canEditNotes="userHasPermission('can_edit_own_notes')"
        :event="activeEvent"
        :calendar="calendar"
        :initCalendarId="theActiveCalendarId"
        :isInDCalendar="true"
        @updated="readEvents"
        @close="closeShowEvent"
        @submitDeleteEventOnward="readEvents(true)">
      </DCalendarShowEvent>
    </transition>
  </div>
</template>

<script>
import moment from 'moment'
import UserPresence from '../Presence/User/PresenceUser'
import Spinner from '../common/Spinner'
import ExportCalendar from '../ExportCalendar/ExportCalendar'
import DWeekplan from '../Weekplan/DWeekplan'
import weekSelector from '../datepicker/WeekSelecter.vue'
import Loader from '../common/Loader'
import DCalendarShowEvent from '../DCalendar/DCalendarShowEvent'
import DCalendar from '../DCalendar/DCalendar'
import UserTasks from '../User/UserTasks'
import SelectCalendar from '../common/SelectCalendar'
import CopyCalendarRotate from '../CopyCalendarRotate/CopyCalendarRotate'
import DCalendarHeader from '../DCalendar/DCalendarHeader'
import DeleteDayOrWeek from '../DeleteDayOrWeek'

export default {
  components: {
    DeleteDayOrWeek,
    DCalendarHeader,
    CopyCalendarRotate,
    DCalendarShowEvent,
    SelectCalendar,
    UserTasks,
    DWeekplan,
    DCalendar,
    Loader,
    Spinner,
    UserPresence,
    ExportCalendar,
    weekSelector
  },
  props: {
    calendarId: {
      default: null
    }
  },
  data () {
    return {
      isShowWeekends: false,
      startTime: '06:00:00',
      endTime: '16:00:00',
      isInPrintMode: false,
      isCalendarInPrintMode: false,
      calendarDates: [],
      isDragging: false,
      debounceTimer: null,
      clickCount: 0,
      clickCountTimer: null,
      isLockedFromFrontend: false,
      isCopyCalendarModalOpen: false
    }
  },
  computed: {
    user () {
      return this.$store.state.user
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    isDeviceMobile () {
      return this.$store.state.isDeviceMobile
    },
    theActiveCalendarId () {
      return this.calendarId ? this.calendarId : this.$route.params.calendar_id
    },
    isUsersCalendar () {
      return this.theActiveCalendarId === this.user.user_calendar_id
    },
    calendar () {
      return this.$store.getters.activeCalendar
    },
    firstDayOfWeek () {
      return this.$store.getters.firstDayOfWeek
    },
    isLocked () {
      return this.calendar && this.calendar.locked
    },
    calendarName () {
      return this.calendar ? this.calendar.name : ''
    },
    isEditable () {
      return !this.isLockedFromFrontend // this.$store.getters.isUserCanEditCalendar
    },
    allUsersInCalIdHash () {
      return this.$store.getters.allUsersInCalIdHash
    },
    isShowWeekPlan () {
      return (this.calendar && this.calendar.type === 'klass') || (this.calendar && this.calendar.type === 'user') || (this.calendar && this.calendar.type === 'other')
    },
    userPermissions () {
      return this.$store.getters.userPermissions
    },
    events () {
      return this.$store.state.calendar.events
    },
    availabilityEvents () {
      return this.$store.state.calendar.availabilityEvents
    },
    dCalendarEvents () {
      if (this.isDragging) {
        return this.events.concat(this.availabilityEvents)
      } else {
        return this.events
      }
    },
    isEventsLoading () {
      return this.$store.state.calendar.isEventsLoading
    },
    activeCalendarDate () {
      return moment(this.$store.state.calendar.date).format('YYYY-MM-DD')
    },
    activeEvent () {
      return this.$store.getters.activeCalendarEvent
    },
    iCalUrl () {
      if (this.calendar && this.calendar.ical_url) {
        return window.location.origin + this.calendar.ical_url
      } else if (this.calendar && this.calendar.ical_link) {
        return window.location.origin + this.calendar.ical_link
      } else {
        return 'loading'
      }
    },
    isNotThisWeek () {
      return moment(this.$store.state.calendar.date).isoWeek() === moment().isoWeek()
    },
    isPermissionsActivatedForSchool () {
      return this.$store.getters.isPermissionsActivatedForSchool
    },
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    weekSelectorStartDate () {
      const calType = this.calendar ? this.calendar.type : 'klass'
      if (this.activeSchoolYear) {
        if (calType === 'klass') {
          return moment(this.activeSchoolYear.student_time_start).startOf('isoweek')
        } else {
          return moment(this.activeSchoolYear.employee_time_start).startOf('isoweek')
        }
      }
    },
    weekSelectorEndDate () {
      let endDate = this.calendar && this.calendar.end_date ? this.calendar && this.calendar.end_date : moment().startOf('isoweek')
      let calType = this.calendar ? this.calendar.type : 'klass'
      if (this.activeSchoolYear) {
        if (calType === 'klass') {
          endDate = moment(this.activeSchoolYear.student_time_end).startOf('isoweek')
        } else {
          endDate = moment(this.activeSchoolYear.employee_time_end).startOf('isoweek')
        }
      }
      return endDate
    },
    getIsAbsenceEditable () {
      const schoolIdsWithoutDeleteFnc = [29045, 40195]
      return schoolIdsWithoutDeleteFnc.includes(this.schoolId) ? this.userHasPermission('can_manage_users') : true
    }
  },
  watch: {
    calendar () {
      if (this.calendar && this.calendar.view_state > -1) {
        const state = this.calendar.view_state * 1
        if (state === 0) {
          this.startTime = '06:00:00'
          this.endTime = '18:00:00'
          this.isShowWeekends = false
        } else if (state === 1) {
          this.startTime = '06:00:00'
          this.endTime = '18:00:00'
          this.isShowWeekends = true
        } else {
          this.startTime = '00:00:00'
          this.endTime = '23:00:00'
          this.isShowWeekends = true
        }
      }
    },
    activeCalendarDate (val, oldVal) {
      this.readEvents(true)
    }
  },
  methods: {
    setIsCopyCalendarModalOpen (bool) {
      this.isCopyCalendarModalOpen = bool
    },
    setCalendarDates (days) {
      this.calendarDates = days
    },
    setTheView (view) {
      let hasTasksInUrl = window.location.href.indexOf('?tasks') > 0
      if (view === 'tasks' && !hasTasksInUrl) {
        window.location.href = window.location.href + '?tasks'
      }
      if (view === 'calendar' && hasTasksInUrl) {
        window.location.href = window.location.href.substr(0, window.location.href.indexOf('?tasks'))
      }
      this.theView = view
    },
    userHasPermission (permission) {
      if (!this.user) {
        return false
      }
      if (this.isPermissionsActivatedForSchool) {
        return this.userPermissions.indexOf(permission) > -1 || this.user.admin
      } else {
        return true
      }
    },
    readCalendar () {
      this.$store.dispatch('readCalendar', this.theActiveCalendarId)
    },
    presenceCall (arrOfPresenceInWeek) {
      // this.$store.commit('updateCalendarPresence', arrOfPresenceInWeek)
      setTimeout(_ => {
        this.readEvents()
      }, 0)
    },
    debounce (func, delay) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(func, delay)
    },
    readEvents (andReadSubjects) {
      const firstDay = this.firstDayOfWeek
      const lastDay = this.firstDayOfWeek + 6
      this.clickCount++
      let obj = {
        calIds: [this.calendarId],
        start: moment(this.activeCalendarDate).isoWeekday(firstDay).format('YYYY-MM-DD 00:00'),
        end: moment(this.activeCalendarDate).isoWeekday(lastDay).endOf('day')
      }
      if (andReadSubjects) {
        obj['andReadSubjects'] = true
      }
      if (this.clickCount === 2) {
        clearTimeout(this.clickCountTimer)
      }
      if (this.clickCount === 1) {
        this.$store.dispatch('readCalendarEvents', obj)
        this.clickCountTimer = setTimeout(() => {
          this.clickCount = 0
        }, 300)
      } else {
        this.debounce(() => {
          this.$store.dispatch('readCalendarEvents', obj)
          this.clickCount = 0
        }, 300) // Adjust the debounce delay (in milliseconds) as needed
      }
    },
    prevWeek () {
      const newDate = moment(this.activeCalendarDate).subtract(7, 'days')
      this.setDate(newDate)
    },
    nextWeek () {
      const newDate = moment(this.activeCalendarDate).add(7, 'days')
      this.setDate(newDate)
    },
    today () {
      this.setDate(moment())
    },
    setDate (date) {
      this.$store.commit('setActiveCalendarDate', date)
      this.$helpers.setCookie('do_calendar_current_date', moment(date).format('YYYY-MM-DD'))
    },
    print () {
      this.isCalendarInPrintMode = true
      setTimeout(_ => {
        this.isInPrintMode = true
        this.$nextTick(_ => {
          // https://stackoverflow.com/questions/52343006/how-to-print-a-part-of-a-vue-component-without-losing-the-style
          const prtHtml = document.getElementById('printCalendar').innerHTML
          // Get all stylesheets HTML
          let stylesHtml = ''
          for (const node of [...document.querySelectorAll('link[rel="stylesheet"][href*="/app/static/css/"], style')]) {
            stylesHtml += node.outerHTML
          }
          // Open the print window
          const WinPrint = window.open('', '', 'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0')

          WinPrint.document.write(`<!DOCTYPE html><html><head>${stylesHtml}</head><body>${prtHtml}</body></html>`)
          var afterPrint = () => {
            WinPrint.close()
            this.isInPrintMode = false
            this.isCalendarInPrintMode = false
          }
          WinPrint.onafterprint = afterPrint
          setTimeout(_ => {
            WinPrint.document.close()
            WinPrint.focus()
            WinPrint.print()
          }, 1000)
        }, 500)
      })
    },
    setActiveEvent (event) {
      this.$store.commit('setActiveCalendarEventId', event.obj.event_id)
    },
    closeShowEvent () {
      this.$store.commit('setActiveCalendarEventId', null)
    },
    handleKeyDown (evt) {
      if (evt.key === 'ArrowLeft' && (evt.ctrlKey || evt.metaKey)) {
        evt.preventDefault()
        this.prevWeek()
        return
      }
      if (evt.key === 'ArrowRight' && (evt.ctrlKey || evt.metaKey)) {
        evt.preventDefault()
        this.nextWeek()
        return
      }
    },
    setIsDragging (bool) {
      this.isDragging = bool
    },
    setIsLockedFromFrontend () {
      this.isLockedFromFrontend = !this.isLockedFromFrontend
      this.readEvents()
    }
  },
  beforeMount () {
    this.$store.commit('setActiveCalendarId', this.calendarId)
    this.readCalendar()
    const cookieDate = this.$helpers.getCookie('do_calendar_current_date')
    if (cookieDate && moment(cookieDate).isValid()) {
      this.$store.commit('setActiveCalendarDate', cookieDate)
      if (moment(cookieDate).format('YYYY-MM-DD') === moment(this.date).format('YYYY-MM-DD')) {
        this.readEvents()
      }
    } else {
      this.$store.commit('setActiveCalendarDate', moment().format('YYYY-MM-DD'))
      this.readEvents()
    }
    const theDevice = this.$helpers.checkUserDevice()
    if (theDevice === 'Mobile') {
      this.isLockedFromFrontend = true
      this.$store.commit('setIsDeviceMobile', true)
      document.querySelector('body').classList.add('device--is-mobile')
    }
  },
  mounted () {
    this.$store.dispatch('analyticsOnVisitedCalendar')
  },
  created () {
    document.removeEventListener('keydown', this.handleKeyDown)
    document.addEventListener('keydown', this.handleKeyDown)
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.handleKeyDown)
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.aniZoomIn-enter-active {
  animation: aniZoomIn .25s;
}

.aniZoomIn-leave-active {
  animation: aniZoomIn .25s reverse;
}

@keyframes aniZoomIn {
  from {
    opacity: 0;
    transform: scale3d(.3, .3, .3);
  }

  50% {
    opacity: 1;
  }
}

.d-calendar__calendar__container {
  position: relative;
  height: calc(100vh - 144px);
  min-height: 650px;
}

.d-calendar__calendar__container.d-calendar__calendar__container--print-mode {
  height: 650px;
}

@media print {
  .d-calendar__calendar__container {
    page-break-after: always;
  }

  .d-calendar__calendar__container {
    height: 650px;
  }
}

.sub-header {
  height: 40px;
  display: flex;
  border-bottom: solid 1px #CCCCCC;
  padding: 0;
  margin: 0;
}

.sub-header__title,
.sub-header a {
  font-weight: bold;
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #3e4450;
  opacity: 0.7;
  text-transform: uppercase;
  border-bottom: 2px solid transparent;
  border-top: 2px solid transparent;
}

.sub-header__title {
  margin-left: 0;
}

.sub-header a {
  margin: 0 20px;
  cursor: pointer;
}

.sub-header__title-divider {
  margin-left: 20px;
}

.sub-header a.active, .sub-header a:hover {
  opacity: 1;
  border-bottom: 2px solid #468AD7;
  transition: all 0.15s ease-in-out;
}

.d-calendar {
  border-radius: 4px;
}

.d-calendar__header {
  padding: 0.75rem 1rem;
  border-radius: 4px;
}

.d-calendar__footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 1rem;
  border-radius: 4px;
}

.d-calendar__header__bottom {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.d-calendar__header__bottom__right {
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
}

.do-calendar {
  box-shadow: 0 0.5rem 1rem -0.125rem rgba(10, 10, 10, .1), 0 0 0 1px rgba(10, 10, 10, .02);
  background: #ffffff;
  border-radius: 4px;
}

.do-calendar__header {
  background: #f6f9fc;
}

.do-calendar__wrapper {
  margin: 0 1rem;
  min-width: 800px;
}

.do-calendar-frontend-lock {
  position: fixed;
  z-index: 123123;
  bottom: 1rem;
  left: calc(65px + 1rem);
  cursor: pointer;
}

.do-calendar-frontend-lock__inner {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 50px;
  height: 15vw;
  width: 15vw;
  background-color: var(--primary);
  border-radius: 50%;
  box-shadow: 0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12);
}

.do-calendar-frontend-lock__inner span {
  margin-left: 16px;
}

</style>



// WEBPACK FOOTER //
// DoCalendar.vue?ab7751de